/**
 * Fuzzy match a query string against a text string.
 * Returns a numeric score (higher = better match) when every character of the query
 * appears in order as a subsequence of the text (case-insensitive).
 * Returns null when the query is not a valid subsequence.
 * Empty query returns 0 (neutral, matches everything).
 *
 * Scoring bonuses:
 * - Prefix match (query at start of text)
 * - Word-boundary match (query char at word boundary)
 * - Consecutive character runs
 */
export function fuzzyMatch(query: string, text: string): number | null {
  if (query.length === 0) {
    return 0; // Empty query matches everything neutrally
  }

  const queryLower = query.toLowerCase();
  const textLower = text.toLowerCase();

  // Find if query is a subsequence of text and track match positions
  const positions: number[] = [];
  let queryIdx = 0;
  let textIdx = 0;

  while (queryIdx < queryLower.length && textIdx < textLower.length) {
    if (queryLower[queryIdx] === textLower[textIdx]) {
      positions.push(textIdx);
      queryIdx++;
    }
    textIdx++;
  }

  // Query is not a subsequence
  if (queryIdx !== queryLower.length) {
    return null;
  }

  // Calculate score
  let score = 100; // Base score

  // Prefix bonus: query starts at beginning of text
  if (positions[0] === 0) {
    score += 50;
  }

  // Word-boundary bonus: first match char is at a word boundary
  if (positions[0] > 0) {
    const charBefore = textLower[positions[0] - 1];
    if (charBefore === ' ' || charBefore === '-' || charBefore === '_') {
      score += 30;
    }
  }

  // Consecutive run bonus: award points for consecutive characters
  let consecutiveBonus = 0;
  for (let i = 1; i < positions.length; i++) {
    if (positions[i] === positions[i - 1] + 1) {
      consecutiveBonus += 20;
    }
  }
  score += consecutiveBonus;

  // Position penalty: penalize matches that occur later in the text
  // Average position matters; earlier is better
  const avgPosition = positions.reduce((a, b) => a + b, 0) / positions.length;
  const positionPenalty = Math.floor(avgPosition / 2);
  score -= positionPenalty;

  // Density bonus: reward matches that are close together
  const span = positions[positions.length - 1] - positions[0] + 1;
  const density = queryLower.length / span;
  score += Math.floor(density * 20);

  return Math.max(0, score); // Ensure score is non-negative
}

/**
 * Rank and filter items by fuzzy matching a query against a key derived from each item.
 * Returns items that match the query, sorted by score (descending).
 * Maintains input order for items with equal scores (stable sort).
 */
export function rankBy<T>(
  items: T[],
  query: string,
  key: (item: T) => string
): T[] {
  // Score all items, keeping only matches
  const scored = items
    .map((item, originalIndex) => {
      const text = key(item);
      const score = fuzzyMatch(query, text);
      return score !== null
        ? { item, score, originalIndex }
        : null;
    })
    .filter((entry): entry is { item: T; score: number; originalIndex: number } =>
      entry !== null
    );

  // Sort by score (descending), then by original index (ascending, for stable sort)
  scored.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return a.originalIndex - b.originalIndex;
  });

  return scored.map((entry) => entry.item);
}
